import { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Building2,
  Coins,
  ScrollText,
  UsersRound,
  FilePlus,
  LayoutGrid,
  LogOut,
  Settings,
  Stamp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { BancoAtivoSelector } from './BancoAtivoSelector';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { logout as httpLogout } from '@/lib/auth';
import { getPerfil } from '@/lib/perfil';
import { useSessionStore } from '@/stores/session';
import { BrandMark, BrandWordmark } from './BrandMark';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  badge?: string | number;
  end?: boolean;
}

const WORK_ITEMS: readonly NavItem[] = [
  { to: '/casos', label: ptBR.shell.sidebar.casos, Icon: LayoutGrid, end: true },
  { to: '/casos/novo', label: ptBR.shell.sidebar.novoCaso, Icon: FilePlus },
];

const ADMIN_ITEMS: readonly NavItem[] = [
  { to: '/perfil', label: ptBR.shell.sidebar.perfil, Icon: User },
  { to: '/config', label: ptBR.shell.sidebar.config, Icon: Settings },
];

// Só o escritório-plataforma (SignCheck Admin) vê o console e a configuração
// comercial. O gate é o mesmo do servidor (`is_platform` resolvido no perfil);
// esconder o item é conforto — quem digitar a URL recebe /403 e, na porta do
// servidor, 403 PLATFORM_REQUIRED.
const PLATFORM_ITEMS: readonly NavItem[] = [
  { to: '/admin/clientes', label: ptBR.shell.sidebar.adminClientes, Icon: Building2 },
  { to: '/admin/usuarios', label: ptBR.shell.sidebar.adminUsuarios, Icon: UsersRound },
  { to: '/admin/trilha', label: ptBR.shell.sidebar.adminTrilha, Icon: ScrollText },
  { to: '/configuracoes/precificacao', label: ptBR.shell.sidebar.precificacao, Icon: Coins },
];

// E52.F3: a tela de certificação exige plataforma **e** papel certificador —
// o mesmo gate composto do servidor. O titular da plataforma não a vê, porque
// não herda o poder de assinatura que ela antecede (BR-08).
const CERTIFICACAO_ITEM: NavItem = {
  to: '/certificacao',
  label: ptBR.shell.sidebar.certificacao,
  Icon: Stamp,
};

function userInitials(identity: string | null | undefined): string {
  if (!identity) return '—';
  // Nome de pessoa ("João Silva") → "JS"; e-mail → iniciais do local-part.
  const base = identity.includes('@') ? (identity.split('@')[0] ?? '') : identity;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  const a = parts[0]?.[0];
  const b = parts[1]?.[0];
  if (a && b) return (a + b).toUpperCase();
  return base.slice(0, 2).toUpperCase() || '—';
}

/**
 * Identidade exibida no bloco da conta, em três degraus.
 *
 * E30.F1: o nome do perito titular substituiu o rótulo estático "Perito
 * grafotécnico", com o e-mail como fallback (e como linha secundária quando há
 * nome). E56.F1.T03: o nome da PESSOA passa à frente do titular do ESCRITÓRIO —
 * num escritório com mais de uma pessoa, o titular não é quem está logado.
 *
 * A normalização de vazio/whitespace vale nos dois degraus de nome, pela razão
 * da E30.F1.C01 (F-E30.F1-01): `""` passaria como nome válido e o bloco
 * renderizaria em branco em vez de cair no degrau seguinte.
 */
function resolverIdentidade(
  nomePessoa: string | null,
  peritoTitular: string | null,
  email: string | null,
): { nomeResolvido: string | null; displayName: string | null } {
  const nomeResolvido = (nomePessoa?.trim() || null) ?? (peritoTitular?.trim() || null);
  return { nomeResolvido, displayName: nomeResolvido ?? email };
}

export function Sidebar() {
  const token = useSessionStore((s) => s.token); // ux-neutrality:allow auth-token
  const clearToken = useSessionStore((s) => s.clearToken); // ux-neutrality:allow auth-token
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [peritoTitular, setPeritoTitular] = useState<string | null>(null);
  const [nomePessoa, setNomePessoa] = useState<string | null>(null);
  const [isPlatform, setIsPlatform] = useState(false);
  const [isCertificador, setIsCertificador] = useState(false);
  const [bancoAtivo, setBancoAtivoLocal] = useState<string | null>(null);

  useEffect(() => {
    if (!token /* ux-neutrality:allow auth-token */) {
      setEmail(null);
      setPeritoTitular(null);
      setNomePessoa(null);
      setIsPlatform(false);
      setIsCertificador(false);
      return;
    }
    let cancelled = false;
    getPerfil(apiClient)
      .then((p) => {
        if (!cancelled) {
          setEmail(p.email);
          setPeritoTitular(p.perito_titular);
          setNomePessoa(p.display_name);
          setIsPlatform(p.is_platform);
          setIsCertificador(p.role === 'certificador');
          // E57.F2: o contexto vem do SERVIDOR, já conferido contra o vínculo.
          setBancoAtivoLocal(p.banco_ativo);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEmail(null);
          setPeritoTitular(null);
          setNomePessoa(null);
          setIsPlatform(false);
          setIsCertificador(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, token]); // ux-neutrality:allow auth-token

  const { nomeResolvido, displayName } = resolverIdentidade(nomePessoa, peritoTitular, email);

  async function handleLogout() {
    const current = token; // ux-neutrality:allow auth-token
    clearToken(); // ux-neutrality:allow auth-token
    if (current) {
      try {
        await httpLogout(apiClient, current);
      } catch {
        // logout server-side é best-effort
      }
    }
    navigate('/login', { replace: true });
  }

  return (
    <aside
      id="app-sidebar"
      aria-label={ptBR.shell.sidebar.sectionMain}
      className="flex w-full shrink-0 flex-col gap-[26px] border-b border-border bg-bg px-4 py-[22px] md:sticky md:top-0 md:h-screen md:w-[236px] md:border-b-0 md:border-r"
    >
      <Link
        to="/casos"
        aria-label={ptBR.productName}
        className="flex items-center gap-2.5 px-1.5 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <BrandMark />
        <BrandWordmark eyebrow={ptBR.shell.brandSub} />
      </Link>

      <SidebarSection label={ptBR.shell.sidebar.sectionWork} items={WORK_ITEMS} />
      <SidebarSection label={ptBR.shell.sidebar.sectionAdmin} items={ADMIN_ITEMS} />
      {isPlatform ? (
        <SidebarSection
          label={ptBR.shell.sidebar.sectionPlataforma}
          items={isCertificador ? [...PLATFORM_ITEMS, CERTIFICACAO_ITEM] : PLATFORM_ITEMS}
        />
      ) : null}

      <div className="mt-auto flex flex-col gap-2 border-t border-border pt-3.5">
        <BancoAtivoSelector bancoAtivo={bancoAtivo} />
        <div className="flex items-center gap-2.5 px-1 py-1.5">
          <div className="grid h-7 w-7 place-items-center border border-border-strong bg-surface-3 font-mono text-[11px] font-semibold text-fg">
            {userInitials(displayName)}
          </div>
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span
              className="truncate text-[12.5px] text-fg"
              title={displayName ?? undefined}
              data-testid="sidebar-account-name"
            >
              {displayName ?? ptBR.shell.sidebar.userAnonymous}
            </span>
            {nomeResolvido && email ? (
              <span className="truncate font-mono text-[10px] text-fg-muted" title={email}>
                {email}
              </span>
            ) : null}
          </div>
        </div>

        {token /* ux-neutrality:allow auth-token */ ? (
          <button
            type="button"
            onClick={handleLogout}
            data-testid="sidebar-logout"
            aria-label={ptBR.nav.logout}
            className="inline-flex items-center justify-center gap-2 border border-border bg-transparent px-2.5 py-1.5 text-[12.5px] text-fg-muted transition-colors duration-fast ease-sc hover:bg-fg/[0.07] hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{ptBR.nav.logout}</span>
          </button>
        ) : null}
      </div>
    </aside>
  );
}

/**
 * Item de navegação da prancha 1B: 14px, respiro 7px/10px e **borda hairline
 * sempre presente** — transparente em repouso, na cor da divisória quando
 * ativo. A borda constante é o que impede o item de "pular" 1px ao virar
 * ativo, e é como o `.sc-nav a` do pacote é construído.
 *
 * O ativo é campo de aço claro (`accent-100` + `accent-800`), não uma barra
 * lateral: no blueprint quem marca posição é a moldura, não um enfeite.
 */
const ITEM_BASE =
  'flex items-center gap-2.5 border px-2.5 py-[7px] text-[14px] transition-colors duration-fast ease-sc focus-visible:outline-2 focus-visible:outline-accent';
const ITEM_ATIVO = 'border-border bg-accent-100 text-accent-800';
const ITEM_REPOUSO = 'border-transparent text-fg hover:bg-fg/[0.05]';

function SidebarSection({ label, items }: { label: string; items: readonly NavItem[] }) {
  return (
    <nav className="flex flex-col gap-0.5" aria-label={label}>
      <p className="px-2.5 pb-1.5 font-display text-[11px] uppercase tracking-[0.16em] text-fg-dim">
        {label}
      </p>
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) => `${ITEM_BASE} ${isActive ? ITEM_ATIVO : ITEM_REPOUSO}`}
            >
              <item.Icon className="h-[17px] w-[17px] shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge !== undefined ? (
                <span className="font-mono text-[10px] text-fg-muted">{item.badge}</span>
              ) : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
